(() => {
  const STATES = {
    tooth: {
      scene: 'zoom',
      zoom: 1,
      ox: 400,
      oy: 455,
      prisms: 0,
      lattice: 0,
      labels: 1,
      mag: 1,
      bar: '5 mm',
    },
    enamel: {
      scene: 'zoom',
      zoom: 3.35,
      ox: 400,
      oy: 228,
      prisms: 0.22,
      lattice: 0,
      labels: 0.35,
      mag: 45,
      bar: '400 μm',
    },
    crystals: {
      scene: 'zoom',
      zoom: 9.2,
      ox: 418,
      oy: 168,
      prisms: 1,
      lattice: 0.08,
      labels: 0,
      mag: 900,
      bar: '15 μm',
    },
    mineral: {
      scene: 'zoom',
      zoom: 22,
      ox: 418,
      oy: 168,
      prisms: 0.2,
      lattice: 1,
      labels: 0,
      mag: 80000,
      bar: '2 nm',
    },
    remine: {
      scene: 'remine',
      zoom: 1,
      ox: 400,
      oy: 455,
      prisms: 0,
      lattice: 0,
      labels: 0,
      mag: 2500,
      bar: '8 μm',
    },
    scale: {
      scene: 'scale',
      zoom: 1,
      ox: 400,
      oy: 455,
      prisms: 0,
      lattice: 0,
      labels: 0,
      mag: 0,
      bar: 'log',
    },
  };

  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const mixState = (a, b, t) => {
    if (!a) return b;
    if (!b || t <= 0) return a;
    if (t >= 1) return b;
    if (a.scene !== b.scene) return t < 0.45 ? a : b;
    return {
      scene: b.scene,
      zoom: lerp(a.zoom, b.zoom, t),
      ox: lerp(a.ox, b.ox, t),
      oy: lerp(a.oy, b.oy, t),
      prisms: lerp(a.prisms, b.prisms, t),
      lattice: lerp(a.lattice, b.lattice, t),
      labels: lerp(a.labels, b.labels, t),
      mag: lerp(a.mag, b.mag, t),
      bar: t < 0.5 ? a.bar : b.bar,
    };
  };

  const formatMag = (n) => {
    if (!n) return '';
    if (n >= 1000) return `×${Math.round(n).toLocaleString('en-US')}`;
    if (n >= 10) return `×${Math.round(n)}`;
    return '×1';
  };

  const chapterProgress = (el) => {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    const start = vh * 0.42;
    const end = vh * 0.18;
    const t = (start - rect.top) / Math.max(rect.height - (start - end), 1);
    return clamp(t, 0, 1);
  };

  const init = (root) => {
    const stage = root.querySelector('[data-hap-stage]');
    const plate = root.querySelector('[data-hap-plate]');
    const canvas = root.querySelector('[data-hap-canvas]');
    const chapters = [...root.querySelectorAll('[data-hap-chapter]')];
    const nav = [...root.querySelectorAll('[data-hap-nav] a')];
    const fig = root.querySelector('[data-hap-fig]');
    const caption = root.querySelector('[data-hap-caption]');
    const magEl = root.querySelector('[data-hap-mag]');
    const barEl = root.querySelector('[data-hap-bar]');
    const remineBtns = [...root.querySelectorAll('[data-hap-remine-state]')];
    const reminePlay = root.querySelector('[data-hap-remine-play]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!stage || !chapters.length) return;

    let current = { ...STATES.tooth };
    let display = { ...STATES.tooth };
    let activeIndex = 0;
    let raf = 0;
    let inView = false;
    let remineT = 0;
    let remineDir = 1;
    let reminePlaying = !reduced;
    let remineLocked = false;
    let lastCaption = '';
    let lastNav = -1;
    const particles = [];

    const ctx = canvas && canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resizeCanvas = () => {
      if (!canvas || !ctx || !plate) return;
      const rect = plate.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const seedParticles = (w, h) => {
      particles.length = 0;
      const nNano = w < 520 ? 42 : 70;
      const nMicro = w < 520 ? 10 : 16;
      for (let i = 0; i < nNano; i += 1) {
        particles.push({
          kind: 'nano',
          x: 0.08 + Math.random() * 0.84,
          y: 0.14 + Math.random() * 0.18,
          r: 1.2 + Math.random() * 1.4,
          seed: Math.random() * Math.PI * 2,
          pit: Math.random(),
        });
      }
      for (let i = 0; i < nMicro; i += 1) {
        particles.push({
          kind: 'micro',
          x: 0.1 + Math.random() * 0.8,
          y: 0.12 + Math.random() * 0.16,
          r: 4.5 + Math.random() * 3.2,
          seed: Math.random() * Math.PI * 2,
          pit: Math.random(),
        });
      }
    };

    const applyState = (state) => {
      stage.dataset.scene = state.scene;
      stage.style.setProperty('--hap-zoom', state.zoom.toFixed(3));
      stage.style.setProperty('--hap-ox', `${state.ox}px`);
      stage.style.setProperty('--hap-oy', `${state.oy}px`);
      stage.style.setProperty('--hap-prisms', state.prisms.toFixed(3));
      stage.style.setProperty('--hap-lattice', state.lattice.toFixed(3));
      stage.style.setProperty('--hap-labels', state.labels.toFixed(3));
      stage.style.setProperty('--hap-remine', remineT.toFixed(3));
      if (magEl) magEl.textContent = state.mag ? formatMag(state.mag) : '';
      if (barEl) barEl.textContent = state.bar === 'log' ? 'Log scale' : state.bar;
    };

    const setCaption = (chapter) => {
      const nextFig = chapter.dataset.fig || '';
      const nextCaption = chapter.dataset.caption || '';
      if (fig) fig.textContent = nextFig;
      if (caption && nextCaption !== lastCaption) {
        caption.textContent = nextCaption;
        lastCaption = nextCaption;
      }
    };

    const setNav = (index) => {
      if (index === lastNav) return;
      lastNav = index;
      nav.forEach((link, i) => {
        if (i === index) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    };

    const setRemineButtons = () => {
      const slot = remineT < 0.33 ? '0' : remineT < 0.72 ? '1' : '2';
      remineBtns.forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.dataset.hapRemineState === slot ? 'true' : 'false');
      });
      if (reminePlay) {
        reminePlay.setAttribute('aria-pressed', reminePlaying ? 'true' : 'false');
        const pause = reminePlay.dataset.labelPause;
        const play = reminePlay.dataset.labelPlay;
        reminePlay.textContent = reminePlaying ? pause || 'Pause' : play || 'Play';
      }
    };

    const drawRemine = (w, h) => {
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);
      const t = remineT;
      particles.forEach((p) => {
        const sway = Math.sin(p.seed + t * 6) * 0.012;
        const startY = 0.16 + (p.seed % 1) * 0.12;
        const dockY = p.kind === 'nano' ? 0.68 + (p.pit * 0.12) : 0.355 + (p.pit * 0.03);
        const y = lerp(startY, dockY, Math.pow(t, p.kind === 'nano' ? 0.85 : 1.15));
        const x = p.x + sway * (1 - t);
        const alpha = 0.22 + t * 0.55;
        ctx.beginPath();
        ctx.fillStyle =
          p.kind === 'nano'
            ? `rgba(5,5,5,${alpha})`
            : `rgba(42,42,42,${0.18 + t * 0.42})`;
        ctx.arc(x * w, y * h, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const draw = () => {
      if (!ctx || !canvas) return;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (display.scene === 'remine') drawRemine(w, h);
      else ctx.clearRect(0, 0, w, h);
    };

    const readJourney = () => {
      let best = 0;
      let bestScore = -1;
      chapters.forEach((chapter, i) => {
        const rect = chapter.getBoundingClientRect();
        const mid = rect.top + rect.height * 0.28;
        const score = 1 - Math.abs(mid - window.innerHeight * 0.42) / window.innerHeight;
        if (score > bestScore) {
          bestScore = score;
          best = i;
        }
      });
      activeIndex = best;
      const chapter = chapters[best];
      const key = chapter.dataset.scene || 'tooth';
      const target = STATES[key] || STATES.tooth;
      const prev = STATES[chapters[Math.max(0, best - 1)].dataset.scene] || target;
      const t = chapterProgress(chapter);
      const mix = reduced ? 1 : clamp((t - 0.06) / 0.4, 0, 1);
      current = mixState(prev, target, mix);
      setCaption(chapter);
      setNav(best);
    };

    const tick = () => {
      raf = 0;
      if (!inView) return;
      readJourney();
      const ease = reduced ? 1 : 0.16;
      display = mixState(display, current, ease);
      if (current.scene === 'zoom') display = mixState(display, current, reduced ? 1 : 0.22);

      if (current.scene === 'remine' && reminePlaying && !remineLocked && !reduced) {
        remineT += 0.0032 * remineDir;
        if (remineT >= 1) {
          remineT = 1;
          remineDir = -1;
        } else if (remineT <= 0) {
          remineT = 0;
          remineDir = 1;
        }
      }
      applyState(display);
      setRemineButtons();
      draw();
      raf = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (raf || !inView) return;
      raf = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      if (!raf) return;
      window.cancelAnimationFrame(raf);
      raf = 0;
    };

    remineBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const slot = Number(btn.dataset.hapRemineState);
        remineT = slot === 0 ? 0 : slot === 1 ? 0.55 : 1;
        reminePlaying = false;
        remineLocked = true;
        applyState(display);
        setRemineButtons();
        draw();
      });
    });

    if (reminePlay) {
      reminePlay.addEventListener('click', () => {
        reminePlaying = !reminePlaying;
        remineLocked = !reminePlaying;
        if (reminePlaying && remineT >= 1) remineDir = -1;
        setRemineButtons();
      });
    }

    nav.forEach((link) => {
      link.addEventListener('click', (event) => {
        const id = link.getAttribute('href');
        if (!id || id.charAt(0) !== '#') return;
        const target = root.querySelector(id);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        if (history.replaceState) history.replaceState(null, '', id);
      });
    });

    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            inView = entries.some((entry) => entry.isIntersecting);
            if (inView) start();
            else stop();
          },
          { rootMargin: '120px 0px' }
        )
      : null;

    if (io) io.observe(stage);
    else {
      inView = true;
      start();
    }

    resizeCanvas();
    seedParticles(plate.clientWidth, plate.clientHeight);
    applyState(display);
    draw();

    window.addEventListener(
      'resize',
      () => {
        resizeCanvas();
        seedParticles(plate.clientWidth, plate.clientHeight);
        draw();
      },
      { passive: true }
    );
  };

  const roots = document.querySelectorAll('[data-hap-explainer]');
  roots.forEach(init);
})();
